import { styled } from "@linaria/react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { DialogTitle } from "@radix-ui/react-dialog"
import { observer } from "mobx-react-lite"
import { useRef } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { DialogClose, DialogContent, DialogOverlay } from "@/components/Dialog/Dialog.tsx"
import { getDialogOnInteractOutsideHandler, getDialogOnOpenAutoFocusHandler } from "@/components/Dialog/utils.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { CloseLight } from "@/icons/Icons.ts"
import {
  ArrowStroke,
  FigmaConnectBg,
  FigmaStroke,
  MatterStroke,
  SvgIllustration
} from "@/illustrations/Illustrations.ts"
import { HeadingStyles } from "@/styles/Markdown.ts"


interface FigmaConnectDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  connectToFigma: () => void
}

export const FigmaConnectDialog = observer(({ isOpen, onOpenChange, connectToFigma }: FigmaConnectDialogProps) => {
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogOverlay />
        <FigmaDialogContent
          ref={contentRef}
          aria-describedby={undefined}
          onClick={(e) => e.stopPropagation()}
          onEscapeKeyDown={(e) => e.stopPropagation()}
          onOpenAutoFocus={getDialogOnOpenAutoFocusHandler(contentRef)}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={getDialogOnInteractOutsideHandler()}
        >
          <BackgroundSection>
            <FigmaConnectBg className={SvgIllustration} />
            <LogosContainer gap="30px">
              <MatterStroke className={SvgIllustration} />
              <SpacedArrow className={SvgIllustration} />
              <FigmaStroke className={SvgIllustration} />
            </LogosContainer>

            <Tooltip content="Close">
              <DialogClose asChild>
                <Button kind="icon">
                  <CloseLight color="#FFF" />
                </Button>
              </DialogClose>
            </Tooltip>
          </BackgroundSection>

          <ContentSection>
            <TextSection>
              <FigmaDialogTitle className={HeadingStyles.Heading2}>Connect Matter to Figma</FigmaDialogTitle>
              Allow access to your Figma account to let the agent generate and refine UI based on your mockups and
              components
            </TextSection>

            <ButtonSection>
              <Button kind="secondary" size="large" onClick={() => {
                connectToFigma()
                onOpenChange(false)
              }}>
                Connect to Figma
              </Button>
            </ButtonSection>
          </ContentSection>
        </FigmaDialogContent>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
})


const FigmaDialogContent = styled(DialogContent)`
  max-width: 460px;
  max-height: 90vh;

  padding: 0;
  border-radius: var(--border-radius-32);

  overflow-y: auto;
`

const BackgroundSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;
  position: relative;

  height: 230px;

  background: #000;
`

const FigmaDialogTitle = styled(DialogTitle)`
  font-weight: 500;
`

const LogosContainer = styled(Horizontal)`
  align-items: center;

  position: absolute;
`

const SpacedArrow = styled(ArrowStroke)`
  margin-right: 12px;
`

const ContentSection = styled(Vertical)`
  flex: 1;
  gap: 24px;

  padding: 24px;
`

const TextSection = styled(Vertical)`
  gap: 12px;

  text-align: center;
`

const ButtonSection = styled.div`
  display: flex;
  justify-content: center;

  padding-bottom: 16px;
`
